export const keywordGap = {
  title: 'Keyword Gap',
  status: {
    pending: 'Pending',
    running: 'Running',
    completed: 'Completed',
    failed: 'Failed',
  },
  actions: {
    ok: {
      badge: 'OK',
      title: 'Maintain',
      description: 'You rank well with the right URL. Monitor and preserve.',
    },
    optimize: {
      badge: 'Optimize',
      title: 'Improve ranking',
      description: 'The URL is the right one but there is room to climb the rankings.',
    },
    rematch: {
      badge: 'Re-match',
      title: 'Re-target signals',
      description: 'You rank with a URL that is not the best one for that search intent.',
    },
    create: {
      badge: 'Create',
      title: 'Create content',
      description: 'You have no content on these topics. Opportunities to capture new traffic.',
    },
    skip: {
      badge: 'Skip',
      title: 'Out of scope',
      description: "Keywords that don't fit your product.",
    },
  },
  strength: {
    hard: 'Hard',
    medium: 'Medium',
    quick: 'Quick win',
  },
  rationale: {
    'ok.withPosition': 'You rank at #{{position}} with the right URL.',
    'ok.withoutPosition': 'You rank with the right URL.',
    'optimize.withPosition':
      'You rank at #{{position}} with the right URL. Room to reach top {{targetPosition}}.',
    'optimize.withoutPosition': 'Your URL is the right one. Room to improve ranking.',
    'rematch.withBothUrls':
      'You rank with {{currentUrl}}, but the intent matches {{suggestedUrl}} better.',
    'rematch.withSuggestedOnly':
      'Your URL {{suggestedUrl}} is the right one for this intent but is not the one ranking.',
    'rematch.generic': 'The ranking URL does not match the search intent.',
    'create.withCompetitorPosition':
      'You have no content on this topic. Your best competitor ranks at #{{position}}.',
    'create.withoutCompetitorPosition':
      'You have no content on this topic: an uncovered opportunity.',
    'skip.outOfScope': 'Out of scope.',
  },
  summary: {
    opportunitiesLabel: 'actionable opportunities',
    volumeLabel: 'searches/month',
    fromAnalyzed: 'from {{total}} keywords analyzed',
    concentration: 'Most concentrated in {{action}} ({{pct}}%).',
    noOpportunities: 'No actionable opportunities found in this analysis.',
  },
  list: {
    title: 'Keyword gap analyses',
    subtitle: 'Spot opportunities by comparing your site against competitors.',
    loading: 'Loading analyses…',
    error: 'Could not load analyses.',
    empty: 'No analyses yet. Launch your first to get started.',
    emptyTitle: 'No analyses yet',
    emptyFiltered: 'No analyses match the applied filters.',
    emptyFilteredTitle: 'No results',
    resultCount_one: '{{count}} analysis',
    resultCount_other: '{{count}} analyses',
    newAction: 'New analysis',
    vsClient: 'vs {{client}}',
    noCompetitors: 'No competitors',
    inProgressHint: 'Analysis still in progress',
    strategyShort: {
      by_volume: 'Volume',
      by_traffic_potential: 'Traffic',
      striking_distance: 'Striking',
    },
    cols: {
      client: 'Site',
      target: 'Competitors',
      market: 'Market',
      strategy: 'Strategy',
      preset: 'Focus',
      status: 'Status',
      date: 'Date',
    },
    deleteDialog: {
      action: 'Delete analysis',
      title: 'Delete analysis',
      description:
        'This action cannot be undone. The analysis and all its results will be permanently deleted.',
      cancel: 'Cancel',
      confirm: 'Delete',
      deleting: 'Deleting...',
      success: 'Analysis deleted.',
      error: 'Could not delete the analysis.',
    },
  },
  history: {
    filters: {
      clientLabel: 'Site',
      clientPlaceholder: 'Search by site',
      competitorLabel: 'Competitor',
      competitorPlaceholder: 'Search by competitor',
      marketLabel: 'Market',
      marketPlaceholder: 'Market',
      allMarkets: 'All markets',
      fromLabel: 'From',
      toLabel: 'To',
      clear: 'Clear filters',
      dateRange: {
        placeholder: 'Date',
        apply: 'Apply',
        cancel: 'Cancel',
        clear: 'Clear',
        presets: {
          last7Days: 'Last 7 days',
          last30Days: 'Last 30 days',
          last90Days: 'Last 90 days',
          monthToDate: 'Month to date',
          yearToDate: 'Year to date',
        },
      },
    },
    pagination: {
      indicator: 'Page {{page}} of {{total}}',
      previous: 'Previous',
      next: 'Next',
    },
  },
  new: {
    title: 'New analysis',
    subtitle:
      'Compare your domain against competitors to surface keywords they rank for and you don’t.',
  },
  detail: {
    backToList: 'Back to list',
    versus: 'vs {{competitors}}',
    time: {
      justNow: 'just now',
      minutesAgo: '{minutes}m ago',
      hoursAgo: '{hours}h ago',
      daysAgo: '{days}d ago',
    },
    error: 'Could not load the analysis.',
    searchVolume: 'Search volume',
    clientPosition: 'Your position',
    clusterKeywordCount_one: '{{count}} keyword',
    clusterKeywordCount_other: '{{count}} keywords',
    running: {
      title: 'Your analysis is running',
      subtitle:
        'This can take a few minutes. The page will refresh automatically when it finishes.',
    },
    failed: {
      title: 'The analysis failed',
    },
    noLabsData: {
      title: 'No Keyword Gap data for {{country}}',
      description:
        "DataForSEO doesn't have Keyword Gap data for {{country}}. Run the analysis on another market to see results.",
    },
    tabs: {
      actions: 'Actions',
      map: 'Competitive map',
      table: 'Table',
    },
    actionCard: {
      keywordCount_one: '{{count}} keyword',
      keywordCount_other: '{{count}} keywords',
      volumeLabel: 'Vol/mo',
      empty: 'No keywords in this category.',
      unclustered: 'Ungrouped',
    },
    secondaryActions: {
      title: 'Other categories',
      subtitle: 'Where else your domain stands in this market.',
    },
    brand: {
      client: {
        title: 'Your brand',
        subtitle: 'Keywords mentioning your brand.',
      },
      competitor: {
        title: 'Competitor brands',
        subtitle: 'Keywords mentioning competitors.',
      },
    },
    skip: {
      title: 'Skip',
      subtitle: 'Keywords out of your product scope.',
    },
    map: {
      title: 'Competitive map',
      subtitle: 'Volume vs. difficulty. Each dot is a keyword.',
      xAxis: 'Difficulty (KD)',
      yAxis: 'Monthly volume',
      empty: 'Not enough data for the map.',
    },
    selection: {
      count_one: '{{count}} keyword selected',
      count_other: '{{count}} keywords selected',
      createContentPlan: 'Plan content',
      clear: 'Clear selection',
    },
  },
  form: {
    client: {
      label: 'Your domain',
      placeholder: 'Pick a domain',
      empty: "You don't have any websites configured. Add one in Configuration.",
    },
    market: {
      label: 'Market',
      placeholder: 'Pick a market',
      noLabsHint: 'No Keyword Gap data',
      countries: {
        CO: 'Colombia',
        AR: 'Argentina',
        MX: 'Mexico',
        ES: 'Spain',
        CL: 'Chile',
        PE: 'Peru',
        BR: 'Brazil',
        GT: 'Guatemala',
        US: 'United States',
        GB: 'United Kingdom',
        DE: 'Germany',
        FR: 'France',
      },
    },
    competitors: {
      label: 'Competitors to analyze',
      empty: "You don't have any competitors saved. Add at least one using the field below.",
      adHocLabel: 'Add competitor by domain',
      adHocPlaceholder: 'e.g. competitor.com',
      addAdHoc: 'Add competitor',
      removeAdHoc: 'Remove competitor',
    },
    advanced: {
      strategy: {
        label: 'Selection strategy',
        options: {
          by_volume: {
            label: 'By volume',
            description: 'Keywords with the highest monthly search volume.',
          },
          by_traffic_potential: {
            label: 'By traffic potential',
            description: "The ones driving the most estimated traffic to your competitors.",
          },
          striking_distance: {
            label: 'Striking distance',
            description: 'Where you already rank 11-30: nearby opportunities to reach top 10.',
          },
        },
      },
      volumePreset: {
        label: 'Analysis focus',
        options: {
          discovery: {
            label: 'Discovery',
            description: 'Balanced: meaningful volume without excessive noise.',
          },
          long_tail: {
            label: 'Long-tail',
            description: 'Low volume, high precision: specific niches for focused topics.',
          },
          high_volume: {
            label: 'High volume',
            description: 'Massive searches: more reach but also more noise and difficulty.',
          },
          custom: {
            label: 'Custom',
            description: 'Define your own min and max bounds.',
          },
        },
      },
      volumeMin: 'Min volume',
      volumeMax: 'Max volume',
      volumeMinShort: 'Min',
      volumeMaxShort: 'Max',
      cap: {
        label: 'Keyword cap',
        hint: 'How many keywords to analyze at most. More keywords = deeper analysis but slower.',
      },
    },
    submit: 'Launch analysis',
    submitting: 'Launching…',
    submitError: 'Could not launch the analysis. Check your inputs and try again.',
    errors: {
      clientDomainRequired: 'Domain is required.',
      marketRequired: 'Pick a market.',
      competitorsMin: 'Pick at least one competitor.',
      competitorsMax: 'Maximum 4 competitors per analysis.',
      adHocDomainRequired: 'Domain cannot be empty.',
      volumeRange: 'Min volume cannot be greater than max volume.',
    },
  },
  coverage: {
    title: 'Data coverage',
    subtitle: 'The more complete, the better the results.',
    score: '{{done}}/{{total}}',
    checks: {
      website: {
        label: 'Your website',
        doneHint: 'You have at least one website configured.',
        pendingHint: 'Add your main website.',
      },
      competitors: {
        label: 'Competitors',
        doneHint: 'Competitors are loaded.',
        pendingHint: 'Add at least one competitor to enrich the analysis.',
      },
      orgContext: {
        label: 'Business context',
        doneHint: 'All of your company details are filled in.',
        pendingHint: 'Fill in all of your company details.',
      },
      knowledgeBase: {
        label: 'Knowledge base',
        doneHint: 'Your site is indexed in the KB.',
        pendingHint: 'Index your site in the KB for sharper suggestions.',
      },
    },
  },
  categories: {
    shared: 'Shared',
    missing: 'Missing',
    weak: 'Weak',
    strong: 'Strong',
    untapped: 'Untapped',
    unique: 'Unique',
    all: 'All',
  },
  intent: {
    informational: {
      name: 'Informational',
      description: 'The user is looking for an answer to a specific question or general topic.',
    },
    navigational: {
      name: 'Navigational',
      description: 'The user wants to find a specific page or site.',
    },
    commercial: {
      name: 'Commercial',
      description: 'The user is researching products, services, or brands before buying.',
    },
    transactional: {
      name: 'Transactional',
      description: 'The user is ready to complete a purchase or take an action.',
    },
    unknown: {
      name: 'Unclassified',
      description: 'No intent data available for this keyword.',
    },
  },
  competitiveMap: {
    title: 'Competitive map',
    youMarker: 'you',
    axisX: 'Universe',
    axisY: 'Overlap',
    quadrant: {
      nicheRival: 'Niche rivals',
      directRival: 'Direct rivals',
      peripheral: 'Peripheral',
      parallel: 'Parallel markets',
    },
    tooltip: {
      universe: 'Universe',
      overlap: 'Overlap',
      missing: 'Missing',
    },
  },
  table: {
    select: 'Select',
    selectAll: 'Select all',
    keyword: 'Keyword',
    intent: 'Intent',
    volume: 'Volume',
    cpc: 'CPC',
    kd: 'KD',
    pageOf: 'Page {{page}} of {{total}}',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    tooltip: {
      intent: {
        title: 'Search intent',
        description:
          'What the user wants when searching. Informational (learn), Navigational (find a site), Commercial (research before buying) or Transactional (buy/take action).',
      },
      position: {
        title: 'Google ranking',
        description:
          'Where the domain shows up in organic results. Lower numbers are better. "—" means the domain does not rank in the top 100.',
      },
      volume: {
        title: 'Search volume',
        description:
          'Estimated monthly searches for this keyword in the selected market.',
      },
      cpc: {
        title: 'Cost per click (CPC)',
        description:
          'What an advertiser would pay on average per click on a Google Ads ad for this keyword. A proxy for commercial value.',
      },
      kd: {
        title: 'Keyword difficulty',
        description:
          'How hard it is to rank in the top 10 for this keyword. 0–100 scale: green (≤30) is easy, amber (31–60) medium, red (>60) hard.',
      },
    },
  },
  filters: {
    minVolume: 'Min volume',
    searchKeyword: 'Search keyword',
    exportCsv: 'Export CSV',
  },
} as const
